// ============================================
// UkuleleChordEngine - Motor de Acordes para Ukelele (GCEA)
// ============================================

import {
  noteToSemitones,
  semitonesToNote,
  shouldUseFlats,
  getNoteAtPosition,
  type NoteName,
  type NeckPosition,
} from "./guitarEngine";

export type UkuleleChordQuality =
  | "major" | "minor" | "7" | "maj7" | "m7" | "dim" | "aug"
  | "sus2" | "sus4" | "6" | "m6" | "9" | "m9" | "add9";

export interface UkuleleChordDef {
  quality: UkuleleChordQuality;
  label: string;
  category: string;
  intervals: number[];
  degreeNames: string[];
}

export interface UkuleleShape {
  name: string;
  baseRoot: NoteName; // Nota en la que fue definida este shape
  positions: { string: number; fret: number; finger?: number }[];
  barre?: { fromString: number; toString: number; fret: number };
  mutedStrings?: number[];
}

export interface UkuleleVoicingResult {
  positions: NeckPosition[];
  mutedStrings: number[];
  barre?: { fromString: number; toString: number; fret: number };
  stringCount: number;
  hasOpenStrings: boolean;
  maxFret: number;
  span: number;
}

export interface UkuleleExplorerFilter {
  inversion: "fundamental" | "first" | "second";
  selectedStrings: number[]; // 4=G, 3=C, 2=E, 1=A
  maxFret: number;
  maxSpan: number;
  allowOpenStrings: boolean;
  allowBarre: boolean;
}

// ─── Catálogo de calidades (mismo concepto que guitarra) ───
export const UKULELE_CHORD_CATALOG: UkuleleChordDef[] = [
  { quality: "major", label: "Mayor", category: "Básicos", intervals: [0, 4, 7], degreeNames: ["R", "3", "5"] },
  { quality: "minor", label: "Menor", category: "Básicos", intervals: [0, 3, 7], degreeNames: ["R", "b3", "5"] },
  { quality: "7", label: "7", category: "Básicos", intervals: [0, 4, 7, 10], degreeNames: ["R", "3", "5", "b7"] },
  { quality: "maj7", label: "Maj7", category: "Básicos", intervals: [0, 4, 7, 11], degreeNames: ["R", "3", "5", "7"] },
  { quality: "m7", label: "m7", category: "Básicos", intervals: [0, 3, 7, 10], degreeNames: ["R", "b3", "5", "b7"] },
  { quality: "dim", label: "Dim", category: "Básicos", intervals: [0, 3, 6], degreeNames: ["R", "b3", "b5"] },
  { quality: "aug", label: "Aug", category: "Básicos", intervals: [0, 4, 8], degreeNames: ["R", "3", "#5"] },
  { quality: "sus2", label: "sus2", category: "Tensiones", intervals: [0, 2, 7], degreeNames: ["R", "2", "5"] },
  { quality: "sus4", label: "sus4", category: "Tensiones", intervals: [0, 5, 7], degreeNames: ["R", "4", "5"] },
  { quality: "6", label: "6", category: "Tensiones", intervals: [0, 4, 7, 9], degreeNames: ["R", "3", "5", "6"] },
  { quality: "m6", label: "m6", category: "Tensiones", intervals: [0, 3, 7, 9], degreeNames: ["R", "b3", "5", "6"] },
  { quality: "add9", label: "add9", category: "Tensiones", intervals: [0, 4, 7, 14], degreeNames: ["R", "3", "5", "9"] },
  { quality: "9", label: "9", category: "Extendidos", intervals: [0, 4, 7, 10, 14], degreeNames: ["R", "3", "5", "b7", "9"] },
  { quality: "m9", label: "m9", category: "Extendidos", intervals: [0, 3, 7, 10, 14], degreeNames: ["R", "b3", "5", "b7", "9"] },
];

// ─── Shapes predefinidos para Ukelele (afinación GCEA) ───
// Formas abiertas definidas en Do (C). Se transportan igual que guitarra.
export const UKULELE_PREDEFINED_SHAPES: UkuleleShape[] = [
  // === MAYOR ===
  { name: "C Mayor (abierto)", baseRoot: "C", positions: [{ string: 1, fret: 3, finger: 3 }] },
  { name: "C Mayor (forma C móvil)", baseRoot: "C", positions: [{ string: 3, fret: 0 }, { string: 2, fret: 0 }, { string: 1, fret: 0 }] },
  { name: "F Mayor (abierto)", baseRoot: "F", positions: [{ string: 3, fret: 0 }, { string: 2, fret: 1, finger: 1 }, { string: 1, fret: 0 }] },
  { name: "G Mayor (abierto)", baseRoot: "G", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 0 }, { string: 2, fret: 0 }, { string: 1, fret: 2, finger: 1 }] },
  { name: "A Mayor (abierto)", baseRoot: "A", positions: [{ string: 4, fret: 2, finger: 1 }, { string: 3, fret: 1, finger: 2 }, { string: 1, fret: 0 }] },
  { name: "D Mayor (abierto)", baseRoot: "D", positions: [{ string: 4, fret: 2, finger: 1 }, { string: 3, fret: 2, finger: 1 }, { string: 2, fret: 2, finger: 1 }, { string: 1, fret: 0 }] },
  { name: "E Mayor (abierto)", baseRoot: "E", positions: [{ string: 4, fret: 4, finger: 1 }, { string: 3, fret: 4, finger: 2 }, { string: 2, fret: 4, finger: 3 }, { string: 1, fret: 2, finger: 4 }] },
  { name: "Bb Mayor (barre)", baseRoot: "Bb", positions: [{ string: 4, fret: 3, finger: 1 }, { string: 3, fret: 2, finger: 2 }, { string: 2, fret: 1, finger: 3 }, { string: 1, fret: 1, finger: 3 }], barre: { fromString: 1, toString: 2, fret: 1 } },

  // === MENOR ===
  { name: "Cm (barre)", baseRoot: "C", positions: [{ string: 4, fret: 3, finger: 1 }, { string: 3, fret: 3, finger: 1 }, { string: 2, fret: 3, finger: 1 }, { string: 1, fret: 3, finger: 1 }], barre: { fromString: 1, toString: 4, fret: 3 } },
  { name: "Dm (abierto)", baseRoot: "D", positions: [{ string: 4, fret: 2, finger: 2 }, { string: 3, fret: 2, finger: 2 }, { string: 2, fret: 1, finger: 1 }, { string: 1, fret: 0 }] },
  { name: "Em (abierto)", baseRoot: "E", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 4, finger: 3 }, { string: 2, fret: 3, finger: 2 }, { string: 1, fret: 2, finger: 1 }] },
  { name: "Am (abierto)", baseRoot: "A", positions: [{ string: 4, fret: 2, finger: 2 }, { string: 2, fret: 0 }, { string: 1, fret: 0 }] },
  { name: "Fm (barre)", baseRoot: "F", positions: [{ string: 4, fret: 1, finger: 1 }, { string: 3, fret: 3, finger: 3 }, { string: 2, fret: 3, finger: 3 }, { string: 1, fret: 1, finger: 1 }], barre: { fromString: 1, toString: 4, fret: 1 } },
  { name: "Gm (abierto)", baseRoot: "G", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 2, finger: 2 }, { string: 2, fret: 3, finger: 3 }, { string: 1, fret: 1, finger: 1 }] },

  // === 7 ===
  { name: "C7 (abierto)", baseRoot: "C", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 0 }, { string: 2, fret: 0 }, { string: 1, fret: 1, finger: 1 }] },
  { name: "D7 (abierto)", baseRoot: "D", positions: [{ string: 4, fret: 2, finger: 2 }, { string: 3, fret: 2, finger: 2 }, { string: 2, fret: 2, finger: 2 }, { string: 1, fret: 3, finger: 3 }] },
  { name: "E7 (abierto)", baseRoot: "E", positions: [{ string: 4, fret: 1, finger: 1 }, { string: 3, fret: 2, finger: 2 }, { string: 2, fret: 0 }, { string: 1, fret: 2, finger: 3 }] },
  { name: "G7 (abierto)", baseRoot: "G", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 2, finger: 1 }, { string: 2, fret: 1, finger: 2 }, { string: 1, fret: 2, finger: 3 }] },
  { name: "A7 (abierto)", baseRoot: "A", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 1, finger: 1 }, { string: 2, fret: 0 }, { string: 1, fret: 0 }] },
  { name: "F7 (abierto)", baseRoot: "F", positions: [{ string: 4, fret: 2, finger: 2 }, { string: 3, fret: 3, finger: 3 }, { string: 2, fret: 1, finger: 1 }, { string: 1, fret: 0 }] },
  { name: "Bb7 (barre)", baseRoot: "Bb", positions: [{ string: 4, fret: 3, finger: 2 }, { string: 3, fret: 2, finger: 3 }, { string: 2, fret: 3, finger: 4 }, { string: 1, fret: 1, finger: 1 }], barre: { fromString: 1, toString: 4, fret: 1 } },

  // === maj7 ===
  { name: "Cmaj7 (abierto)", baseRoot: "C", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 0 }, { string: 2, fret: 0 }, { string: 1, fret: 2, finger: 1 }] },
  { name: "Fmaj7 (abierto)", baseRoot: "F", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 0 }, { string: 2, fret: 1, finger: 1 }, { string: 1, fret: 0 }] },

  // === m7 ===
  { name: "Dm7 (abierto)", baseRoot: "D", positions: [{ string: 4, fret: 2, finger: 2 }, { string: 3, fret: 2, finger: 2 }, { string: 2, fret: 1, finger: 1 }, { string: 1, fret: 3, finger: 3 }] },
  { name: "Am7 (abierto)", baseRoot: "A", positions: [{ string: 4, fret: 0 }, { string: 3, fret: 0 }, { string: 2, fret: 0 }, { string: 1, fret: 0 }] },

  // === sus2 / sus4 ===
  { name: "Dsus2 (abierto)", baseRoot: "D", positions: [{ string: 4, fret: 2, finger: 1 }, { string: 3, fret: 2, finger: 1 }, { string: 2, fret: 0 }, { string: 1, fret: 0 }] },
  { name: "Dsus4 (abierto)", baseRoot: "D", positions: [{ string: 4, fret: 2, finger: 1 }, { string: 3, fret: 2, finger: 1 }, { string: 2, fret: 3, finger: 3 }, { string: 1, fret: 0 }] },
];

// ─── Helpers ───

export function getUkuleleQualityCategories(): string[] {
  return [...new Set(UKULELE_CHORD_CATALOG.map((c) => c.category))];
}

export function getUkuleleQualitiesByCategory(category: string): UkuleleChordDef[] {
  return UKULELE_CHORD_CATALOG.filter((c) => c.category === category);
}

export function getUkuleleIntervalSemitones(quality: UkuleleChordQuality): number[] {
  const entry = UKULELE_CHORD_CATALOG.find((c) => c.quality === quality);
  return entry?.intervals ?? [0, 4, 7];
}

export function getUkuleleIntervalDegrees(quality: UkuleleChordQuality): string[] {
  const entry = UKULELE_CHORD_CATALOG.find((c) => c.quality === quality);
  return entry?.degreeNames ?? ["R", "3", "5"];
}

// ─── Transposición de shapes ───

function transposeUkuleleShape(shape: UkuleleShape, targetRoot: NoteName): UkuleleShape {
  const rootSemi = noteToSemitones(shape.baseRoot);
  const targetSemi = noteToSemitones(targetRoot);
  let offset = targetSemi - rootSemi;
  while (offset < 0) offset += 12;

  return {
    ...shape,
    positions: shape.positions.map((p) => ({
      ...p,
      fret: p.fret + offset,
    })),
    barre: shape.barre
      ? {
          ...shape.barre,
          fret: shape.barre.fret + offset,
        }
      : undefined,
  };
}

// ─── Generador Clásico (shapes predefinidos) ───

export function getUkuleleClassicVoicings(
  root: NoteName,
  quality: UkuleleChordQuality
): UkuleleVoicingResult[] {
  const useFlats = shouldUseFlats(root, quality.includes("m") && !quality.includes("maj") ? "minor" : "major");
  const rootSemi = noteToSemitones(root);
  const intervals = getUkuleleIntervalSemitones(quality);
  const degrees = getUkuleleIntervalDegrees(quality);

  // Buscar shapes predefinidos que coincidan con la calidad
  const keyword = quality === "major" ? "" : quality;
  const candidates = UKULELE_PREDEFINED_SHAPES.filter((s) => {
    const name = s.name.toLowerCase();
    const rootMatch = name.includes(root.toLowerCase());
    if (quality === "major") return rootMatch && !name.includes("m") && !name.includes("7") && !name.includes("dim") && !name.includes("aug") && !name.includes("sus") && !name.includes("6") && !name.includes("add") && !name.includes("9");
    if (quality === "minor") return rootMatch && name.includes("m") && !name.includes("7") && !name.includes("6") && !name.includes("9");
    if (quality === "7") return rootMatch && name.includes("7") && !name.includes("m7") && !name.includes("maj7");
    if (quality === "maj7") return rootMatch && name.includes("maj7");
    if (quality === "m7") return rootMatch && name.includes("m7");
    if (quality === "dim") return rootMatch && name.includes("dim");
    if (quality === "aug") return rootMatch && name.includes("aug");
    if (quality === "sus2") return rootMatch && name.includes("sus2");
    if (quality === "sus4") return rootMatch && name.includes("sus4");
    if (quality === "6") return rootMatch && name.includes("6") && !name.includes("m6");
    if (quality === "m6") return rootMatch && name.includes("m6");
    if (quality === "add9") return rootMatch && name.includes("add9");
    if (quality === "9") return rootMatch && name.includes("9") && !name.includes("m9");
    if (quality === "m9") return rootMatch && name.includes("m9");
    return rootMatch && name.includes(keyword);
  });

  const results: UkuleleVoicingResult[] = [];

  for (const shape of candidates) {
    const transposed = transposeUkuleleShape(shape, root);
    const positions: NeckPosition[] = [];
    const usedStrings = new Set<number>();

    for (const p of transposed.positions) {
      const pos = getNoteAtPosition(p.string, p.fret);
      const semi = noteToSemitones(pos.note);
      const intervalIdx = intervals.findIndex((int) => (rootSemi + int) % 12 === semi);
      const degree = intervalIdx >= 0 ? degrees[intervalIdx] : "";

      positions.push({
        string: p.string,
        fret: p.fret,
        note: semitonesToNote(semi, useFlats),
        degree: degree as any,
        highlight: degree === "R" ? "root" : degree === "5" || degree === "b5" || degree === "#5" ? "fifth" : "third",
        finger: p.finger,
      });
      usedStrings.add(p.string);
    }

    const muted: number[] = [];
    for (let s = 4; s >= 1; s--) {
      if (!usedStrings.has(s)) muted.push(s);
    }

    const pressed = positions.filter((p) => p.fret > 0);
    const frets = pressed.map((p) => p.fret);
    const span = frets.length > 1 ? Math.max(...frets) - Math.min(...frets) : 0;
    const hasOpen = positions.some((p) => p.fret === 0);

    results.push({
      positions,
      mutedStrings: muted,
      barre: transposed.barre,
      stringCount: positions.length,
      hasOpenStrings: hasOpen,
      maxFret: Math.max(...positions.map((p) => p.fret)),
      span,
    });
  }

  return results;
}

// ─── Generador Explorador para Ukelele ───

export function generateUkuleleExplorerVoicings(
  root: NoteName,
  quality: UkuleleChordQuality,
  filter: UkuleleExplorerFilter
): UkuleleVoicingResult[] {
  const rawIntervals = getUkuleleIntervalSemitones(quality);
  const rawDegrees = getUkuleleIntervalDegrees(quality);
  const rootSemi = noteToSemitones(root);
  const useFlats = shouldUseFlats(root, quality.includes("m") && !quality.includes("maj") ? "minor" : "major");

  const selectedStrings = [...filter.selectedStrings].sort((a, b) => b - a);
  if (selectedStrings.length === 0) return [];

  let bassVoiceIdx = 0;
  switch (filter.inversion) {
    case "fundamental": bassVoiceIdx = 0; break;
    case "first": bassVoiceIdx = 1; break;
    case "second": bassVoiceIdx = 2; break;
  }
  if (bassVoiceIdx >= rawIntervals.length) bassVoiceIdx = rawIntervals.length - 1;

  const voiceOrder: { interval: number; degree: string; originalIdx: number }[] = [];
  voiceOrder.push({ interval: rawIntervals[bassVoiceIdx], degree: rawDegrees[bassVoiceIdx], originalIdx: bassVoiceIdx });
  for (let i = 0; i < rawIntervals.length; i++) {
    if (i !== bassVoiceIdx) voiceOrder.push({ interval: rawIntervals[i], degree: rawDegrees[i], originalIdx: i });
  }

  const usableVoices = voiceOrder.slice(0, selectedStrings.length);
  const usableStrings = selectedStrings.slice(0, usableVoices.length);

  const voicePositions: { string: number; fret: number; note: NoteName }[][][] = [];
  for (let v = 0; v < usableVoices.length; v++) {
    const voice = usableVoices[v];
    const targetSemi = (rootSemi + voice.interval) % 12;
    voicePositions[v] = [];
    for (let sIdx = 0; sIdx < usableStrings.length; sIdx++) {
      const s = usableStrings[sIdx];
      const positions: { string: number; fret: number; note: NoteName }[] = [];
      for (let f = 0; f <= filter.maxFret; f++) {
        const pos = getNoteAtPosition(s, f);
        if (noteToSemitones(pos.note) === targetSemi) {
          positions.push({ string: s, fret: f, note: semitonesToNote(noteToSemitones(pos.note), useFlats) });
        }
      }
      voicePositions[v].push(positions);
    }
  }

  const scoredResults: { result: UkuleleVoicingResult; score: number }[] = [];
  const seen = new Set<string>();

  function backtrack(
    voiceIdx: number,
    assigned: { string: number; fret: number; note: NoteName; degree: string }[],
    usedStrings: Set<number>
  ) {
    if (voiceIdx === usableVoices.length) {
      const pressed = assigned.filter((n) => n.fret > 0);
      const frets = pressed.map((n) => n.fret);
      const span = frets.length > 1 ? Math.max(...frets) - Math.min(...frets) : 0;
      if (span > filter.maxSpan) return;

      const hasOpen = assigned.some((n) => n.fret === 0);
      if (!filter.allowOpenStrings && hasOpen) return;

      const hasBarre = pressed.length >= 2 && new Set(pressed.map((n) => n.fret)).size < pressed.length;
      if (!filter.allowBarre && hasBarre) return;

      const key = assigned.map((c) => `${c.string}-${c.fret}`).sort().join("|");
      if (seen.has(key)) return;
      seen.add(key);

      const sorted = [...assigned].sort((a, b) => b.string - a.string);

      const neckPositions: NeckPosition[] = sorted.map((c) => ({
        string: c.string,
        fret: c.fret,
        note: c.note,
        degree: c.degree as any,
        highlight: c.degree === "R" ? "root" : c.degree === "5" || c.degree === "b5" || c.degree === "#5" ? "fifth" : "third",
        finger: c.fret === 0 ? 0 : undefined,
      }));

      const muted: number[] = [];
      for (let s = 4; s >= 1; s--) {
        if (!selectedStrings.includes(s)) muted.push(s);
      }

      const fretCounts = new Map<number, number>();
      for (const p of pressed) {
        fretCounts.set(p.fret, (fretCounts.get(p.fret) || 0) + 1);
      }
      let barre: { fromString: number; toString: number; fret: number } | undefined;
      for (const [fret, count] of fretCounts) {
        if (count >= 2) {
          const atFret = sorted.filter((c) => c.fret === fret);
          if (atFret.length >= 2) {
            barre = { fromString: atFret[0].string, toString: atFret[atFret.length - 1].string, fret };
          }
        }
      }

      const result: UkuleleVoicingResult = {
        positions: neckPositions,
        mutedStrings: muted,
        barre,
        stringCount: assigned.length,
        hasOpenStrings: hasOpen,
        maxFret: Math.max(...assigned.map((c) => c.fret)),
        span,
      };

      let score = 0;
      if (span <= 2) score += 40;
      else if (span <= 3) score += 25;
      else if (span <= 4) score += 10;
      const maxF = Math.max(...assigned.map((c) => c.fret));
      if (maxF <= 5) score += 20;
      else if (maxF <= 8) score += 10;
      const openCount = assigned.filter((c) => c.fret === 0).length;
      score += openCount * 5;
      const stringGaps = [];
      for (let i = 1; i < sorted.length; i++) {
        stringGaps.push(sorted[i - 1].string - sorted[i].string);
      }
      if (stringGaps.every((g) => g === 1)) score += 15;

      scoredResults.push({ result, score });
      return;
    }

    const voice = usableVoices[voiceIdx];
    const targetString = usableStrings[voiceIdx];
    const candidates = voicePositions[voiceIdx][voiceIdx];

    for (const cand of candidates) {
      if (usedStrings.has(cand.string)) continue;
      const testFrets = [...assigned.map((n) => n.fret), cand.fret].filter((f) => f > 0);
      if (testFrets.length > 1) {
        const testSpan = Math.max(...testFrets) - Math.min(...testFrets);
        if (testSpan > filter.maxSpan) continue;
      }

      assigned.push({ string: cand.string, fret: cand.fret, note: cand.note, degree: voice.degree });
      usedStrings.add(cand.string);
      backtrack(voiceIdx + 1, assigned, usedStrings);
      assigned.pop();
      usedStrings.delete(cand.string);
    }
  }

  backtrack(0, [], new Set());

  scoredResults.sort((a, b) => b.score - a.score);
  return scoredResults.slice(0, 30).map((s) => s.result);
}
